const text = (value) => (value == null ? "" : String(value).trim());

const valueFrom = (order, fallback, key) =>
  order?.[key] ?? fallback?.[key];

const listFrom = (order, fallback, key) => {
  const value = valueFrom(order, fallback, key);
  return Array.isArray(value) ? value.filter(Boolean) : [];
};

const inputDate = (value) => {
  const raw = text(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toISOString().slice(0, 10);
};

const contactValue = (contact) =>
  contact?._id ||
  [contact?.firstname, contact?.lastname].filter(Boolean).join(" ");

export const orderContactDetails = (order = {}, fallback = {}) => {
  const raw = text(valueFrom(order, fallback, "contact"));
  const contacts = fallback.clientRecord?.contacts || [];
  const selected = contacts.find((contact) => {
    const storedValue = [
      contact.firstname,
      contact.lastname,
      contact.email,
      contact.mobile || contact.phone,
    ]
      .filter(Boolean)
      .join(" ");
    return (
      storedValue === raw ||
      (contact.email && raw.includes(contact.email)) ||
      ((contact.mobile || contact.phone) &&
        raw.includes(contact.mobile || contact.phone))
    );
  });

  if (selected) {
    return {
      contact: contactValue(selected),
      name: [selected.firstname, selected.lastname].filter(Boolean).join(" "),
      email: text(selected.email),
      phone: text(selected.mobile || selected.phone),
    };
  }

  const responseContact = order.contactData || {};
  const emailMatch = raw.match(/[^\s@]+@[^\s@]+\.[^\s@]+/);
  const email = text(responseContact.email || emailMatch?.[0]);
  const emailIndex = email ? raw.indexOf(email) : -1;
  const phone =
    text(responseContact.phone) ||
    (emailIndex >= 0
      ? raw.slice(emailIndex + email.length).trim()
      : text(raw.match(/(\+?\d[\d .()-]{7,})$/)?.[1]));
  const name =
    text(responseContact.name) ||
    (emailIndex >= 0
      ? raw.slice(0, emailIndex).trim()
      : phone
        ? raw.slice(0, raw.lastIndexOf(phone)).trim()
        : raw);

  return { contact: "", name, email, phone };
};

const normalizeContractType = (value) => {
  const contract = text(value);
  const knownTypes = {
    interim: "Intérim",
    intérim: "Intérim",
    cdd: "CDD",
    cdi: "CDI",
    vacation: "Vacation",
    stage: "Stage",
    apprentissage: "Apprentissage",
    freelance: "Freelance",
  };
  return knownTypes[contract.toLocaleLowerCase("fr-FR")] || "Intérim";
};

const periodLabel = (period) => {
  const start = inputDate(period?.startDate);
  const end = inputDate(period?.endDate);
  const dates =
    start && end && start !== end ? `du ${start} au ${end}` : start || end;
  const hours = [text(period?.startHour), text(period?.endHour)]
    .filter(Boolean)
    .join("–");
  return [dates, hours].filter(Boolean).join(" · ");
};

export const buildAnnouncementDraft = (order = {}, fallback = {}) => {
  const get = (key) => valueFrom(order, fallback, key);
  const contact = orderContactDetails(order, fallback);
  const client =
    (typeof order.client === "object" ? order.client?._id : order.client) ||
    fallback.client?._id ||
    fallback.client ||
    "";
  const dateRanges = (
    listFrom(order, fallback, "dateRanges").length
      ? listFrom(order, fallback, "dateRanges")
      : [
          {
            startDate: get("startDate"),
            endDate: get("endDate"),
            startHour: get("startHour"),
            endHour: get("endHour"),
          },
        ]
  ).filter((period) => period.startDate || period.endDate);
  const startDate =
    dateRanges[0]?.startDate || get("startDate") || get("dateDebutSouhaitee");
  const schedule = [
    get("nbHeureHebdo") ? `${get("nbHeureHebdo")} h/semaine` : "",
    get("startHour") || get("endHour")
      ? [text(get("startHour")), text(get("endHour"))]
          .filter(Boolean)
          .join("–")
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const consigne = text(get("consigne"));
  const description = [
    text(get("task") || get("description")),
    consigne && !/^0\s*:\s*aucune$/i.test(consigne)
      ? `Consignes :\n${consigne}`
      : "",
  ]
    .filter(Boolean)
    .join("\n\n");
  const diplomas = listFrom(order, fallback, "diplomes");
  const prerequisites = [
    diplomas.length ? `Diplômes : ${diplomas.join(", ")}` : "",
    listFrom(order, fallback, "habilitations").length
      ? `Habilitations : ${listFrom(order, fallback, "habilitations").join(", ")}`
      : "",
    listFrom(order, fallback, "caces").length
      ? `CACES : ${listFrom(order, fallback, "caces").join(", ")}`
      : "",
    listFrom(order, fallback, "permis").length
      ? `Permis : ${listFrom(order, fallback, "permis").join(", ")}`
      : "",
    get("isVehicule") ? "Véhicule requis" : "",
  ]
    .filter(Boolean)
    .join("\n");
  const periods = dateRanges.map(periodLabel).filter(Boolean);
  const primes = listFrom(order, fallback, "primes")
    .map((prime) =>
      [prime.prime, prime.primeType, prime.taux].filter(Boolean).join(" · "),
    )
    .filter(Boolean);
  const additionalInformation = [
    Number(get("quantity")) > 1
      ? `Nombre de postes : ${get("quantity")}`
      : "",
    periods.length
      ? `Période${periods.length > 1 ? "s" : ""} :\n${periods.join("\n")}`
      : "",
    text(get("motif")) ? `Motif : ${text(get("motif"))}` : "",
    text(get("justificatif"))
      ? `Précision : ${text(get("justificatif"))}`
      : "",
    primes.length ? `Primes / indemnités :\n${primes.join("\n")}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");
  const hourlyRate = text(get("hourlyRate") || get("salaire"));

  return {
    title: text(get("qualification") || get("title")),
    client: text(client),
    contact: text(contact.contact),
    localisation: [
      text(get("address")),
      text(get("postCode")),
      text(get("town")),
    ]
      .filter(Boolean)
      .join(" "),
    description,
    profilRecherche: prerequisites,
    jobType: normalizeContractType(get("typeContrat") || get("contractType")),
    salaire: hourlyRate ? `${hourlyRate} € / heure` : "",
    baseSalary: "Heure",
    dateDebutSouhaitee: inputDate(startDate),
    tempTravail: schedule,
    email: contact.email,
    phone: contact.phone,
    informationsComplementaires: additionalInformation,
    niveauQualification: diplomas.join(", "),
    experience: "",
    deplacement: get("isVehicule") ? "Véhicule requis" : "",
    presence: get("address") || get("town") ? "Sur site" : "",
  };
};
